import React, { useState, useMemo } from "react";
import { toast } from "sonner";
import client, { formatApiError } from "../../lib/api";
import { ManualPunchModal, BulkPunchModal } from "./EventModals";

const STALE_THRESHOLD_HOURS = 12;

function hoursSince(iso) {
  if (!iso) return 0;
  const then = new Date(iso).getTime();
  if (isNaN(then)) return 0;
  return (Date.now() - then) / 3600000;
}

function fmtDuration(hrs) {
  if (hrs < 1) return `${Math.max(1, Math.round(hrs * 60))}m`;
  const h = Math.floor(hrs);
  const m = Math.round((hrs - h) * 60);
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function LiveBoard({ branches, employees, onRefresh, onOffenderJump }) {
  const [manualFor, setManualFor] = useState(null);
  const [fillDay, setFillDay] = useState(null);
  const [closing, setClosing] = useState(null); // employee being closed
  const [busyId, setBusyId] = useState(null);

  const stale = useMemo(() => {
    // Use open_since (cross-day) rather than since (today-only) so forgotten
    // punch-outs that crossed UTC midnight still surface.
    return employees.filter((e) => {
      if (!e.open_since) return false;
      return hoursSince(e.open_since) >= STALE_THRESHOLD_HOURS;
    });
  }, [employees]);

  const closeShift = async (emp) => {
    setBusyId(emp.id);
    try {
      // Fire a single owner-side manual punch-out at NOW. If the employee's
      // last open event is break_end we're already in; if it's in we're in.
      // Either way, type=out closes cleanly. (Mid-break staff aren't flagged
      // as open_since — their last event is break_start — so we don't need
      // the break_end preamble here.)
      const tz = -new Date().getTimezoneOffset();
      await client.post("/events/manual", {
        employee_id: emp.id,
        type: "out",
        ts: new Date().toISOString(),
        tz_offset_min: tz,
      });
      toast.success(`Closed ${emp.name}'s shift`);
      setClosing(null);
      onRefresh && onRefresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      {stale.length > 0 && (
        <div
          data-testid="stale-shift-banner"
          className="mb-5 rounded-xl border border-jewel-amber/60 bg-jewel-amber/10 px-4 py-3"
        >
          <div className="caps text-jewel-amber mb-1.5">
            ⚠ {stale.length} forgotten punch-out{stale.length === 1 ? "" : "s"}
          </div>
          <div className="text-[12px] text-muted-jewel mb-2">
            On the clock for {STALE_THRESHOLD_HOURS}h+. Close before it becomes a 24-hour ghost shift.
          </div>
          <div className="space-y-1.5">
            {stale.map((e) => (
              <div
                key={e.id}
                className="flex justify-between items-center text-[13px]"
                data-testid={`stale-row-${e.id}`}
              >
                <span>
                  <span className="font-semibold text-ivory">{e.name}</span>
                  <span className="text-muted-jewel"> · {e.branch} · </span>
                  <span className="text-jewel-amber">{fmtDuration(hoursSince(e.open_since))} ago</span>
                </span>
                <button
                  onClick={() => setClosing(e)}
                  disabled={busyId === e.id}
                  data-testid={`stale-close-${e.id}`}
                  className="text-[11px] uppercase tracking-wider seal-outline rounded-full px-3 py-1 disabled:opacity-50"
                >
                  Close
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {branches.length === 0 && (
        <div className="text-muted-jewel text-sm">No branches configured.</div>
      )}
      {branches.map((b) => {
        const staff = employees.filter((e) => e.branch === b);
        return (
          <div key={b} className="mb-7" data-testid={`live-branch-${b}`}>
            <div className="font-serif text-[22px] font-semibold mb-2.5">{b}</div>
            {staff.length === 0 && (
              <div className="text-muted-jewel text-sm">No staff yet.</div>
            )}
            {staff.map((s) => {
              const st = s.state || "out";
              const onClock = st === "in" || st === "break" || !!s.open_since;
              const info =
                st === "in"
                  ? { color: "#7fc98f", label: "On the clock", cls: "text-jewel-green" }
                  : st === "break"
                    ? { color: "#e0b25c", label: "On break", cls: "text-jewel-amber" }
                    : s.open_since
                      ? { color: "#d57b7b", label: "Still clocked in", cls: "text-jewel-red" }
                      : { color: "#a598ad", label: "Off", cls: "text-muted-jewel" };
              return (
                <div
                  key={s.id}
                  className="flex justify-between items-center py-3 border-b border-line"
                  data-testid={`live-emp-${s.id}`}
                >
                  <button
                    onClick={() => setManualFor(s)}
                    data-testid={`live-punch-${s.id}`}
                    className="text-left flex-1 hover:opacity-80 transition-opacity"
                    title="Owner punch on their behalf"
                  >
                    <div className="text-base">{s.name}</div>
                    <div className="text-[11px] text-muted-jewel">Tap to punch on their behalf</div>
                  </button>
                  <div className="flex items-center gap-2">
                    {onClock && (
                      <button
                        onClick={(ev) => { ev.stopPropagation(); setClosing(s); }}
                        disabled={busyId === s.id}
                        data-testid={`live-close-${s.id}`}
                        title="Close this shift now"
                        className="text-[11px] uppercase tracking-wider seal-outline rounded-full px-3 py-1 disabled:opacity-50"
                      >
                        Close
                      </button>
                    )}
                    <span className={`text-[13px] inline-flex items-center gap-2 ${info.cls}`}>
                      <span className="w-[7px] h-[7px] rounded-full" style={{ background: info.color }} />
                      {info.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}

      {closing && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-5"
          data-testid="close-shift-confirm"
          onClick={() => busyId !== closing.id && setClosing(null)}
        >
          <div
            className="bg-panel border border-line rounded-xl p-5 max-w-sm w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="font-serif text-[18px] font-semibold mb-2">
              Close {closing.name}'s shift?
            </div>
            <div className="text-[13px] text-muted-jewel mb-4">
              Punch them out now ({new Date().toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })}
              {closing.state === "break" ? " — break will be ended first" : ""}
              ). You can edit the time later in Records.
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setClosing(null)}
                disabled={busyId === closing.id}
                data-testid="close-shift-cancel"
                className="flex-1 rounded-lg border border-line text-muted-jewel px-4 py-2.5 text-sm hover:text-ivory disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => closeShift(closing)}
                disabled={busyId === closing.id}
                data-testid="close-shift-confirm-btn"
                className="flex-1 seal-primary rounded-lg px-4 py-2.5 text-sm font-semibold disabled:opacity-50"
              >
                {busyId === closing.id ? "Closing…" : "Close shift"}
              </button>
            </div>
          </div>
        </div>
      )}

      {manualFor && (
        <ManualPunchModal
          employee={manualFor}
          onClose={() => setManualFor(null)}
          onSaved={() => { setManualFor(null); onRefresh && onRefresh(); }}
          onFillDay={(p) => { setManualFor(null); setFillDay(p); }}
          onOffenderJump={onOffenderJump ? (offender) => { setManualFor(null); onOffenderJump(offender); } : undefined}
        />
      )}
      {fillDay && (
        <BulkPunchModal
          employees={employees}
          prefill={fillDay}
          onClose={() => setFillDay(null)}
          onSaved={() => { setFillDay(null); onRefresh && onRefresh(); }}
        />
      )}
    </div>
  );
}

export default LiveBoard;
